import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import PublicHeader from "@/components/PublicHeader";
import { ArrowLeft, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignIn() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const invitationToken = params.get("invitation");
  const presetEmail = params.get("email") || "";
  // Referral + post-signup intent — without these, the submit() handler crashed
  // with ReferenceError on any /signup?ref=… / ?remix=… / ?duplicate=… URL.
  const refCode = params.get("ref");
  const remixToken = params.get("remix");
  const duplicateToken = params.get("duplicate");

  const [name, setName] = useState("");
  const [institution, setInstitution] = useState("");
  const [email, setEmail] = useState(presetEmail);
  const [password, setPassword] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invitePreview, setInvitePreview] = useState(null);

  useEffect(() => {
    if (!invitationToken) return;
    (async () => {
      try {
        const { data } = await api.get(`/institutions/invitations/${invitationToken}/preview`);
        setInvitePreview(data);
        if (data.email) setEmail(data.email);
        setInstitution(data.institution_name || "");
      } catch {
        toast.error("Invitation invalid or used");
      }
    })();
  }, [invitationToken]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!agreed) {
      toast.error("Please agree to Terms and Privacy Policy to continue");
      return;
    }
    setBusy(true);
    try {
      const u = await register({
        name,
        email,
        password,
        institution: institution || null,
        invitation_token: invitationToken || undefined,
      });
      toast.success(`Welcome to TeeBee, ${u.name.split(" ")[0]}!`);

      // Redeem referral code (fire-and-forget)
      if (refCode) {
        try {
          await api.post("/referrals/redeem", { code: refCode });
          toast.info(`Code ${refCode} redeemed — earn 50 coins after your first AI generation.`);
        } catch (e) { logQuietError("signup.referral.redeem", e); }
      }

      // Decide redirect based on intent
      if (remixToken) {
        navigate(`/l/${remixToken}`);
      } else if (duplicateToken) {
        navigate(`/l/${duplicateToken}`);
      } else {
        navigate(invitePreview ? "/institution" : "/dashboard");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen tb-auth-bg" data-testid="signup-page">
      <PublicHeader />
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-3.5rem)]">
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden">
        <div /> {/* spacer (header took the top slot) */}

        {/* Playful gradient blobs */}
        <div aria-hidden className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-emerald-300/30 via-amber-300/20 to-transparent blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-24 -left-16 w-[380px] h-[380px] rounded-full bg-gradient-to-tr from-violet-400/25 via-blue-300/15 to-transparent blur-3xl" />

        <div className="relative flex flex-col items-center gap-6">
          <Logo variant="hero" to={null} className="drop-shadow-[0_10px_30px_rgba(16,185,129,0.18)] tb-float" />

          <div className="tb-glass rounded-3xl p-7 max-w-md w-full">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-700 dark:text-emerald-300">
              The teacher&apos;s productivity OS
            </p>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li>✦ Plan a 45-minute lesson in 20 seconds</li>
              <li>✦ Scan a handwritten notebook into editable text</li>
              <li>✦ Auto-grade descriptive answers with feedback</li>
              <li>✦ Run QR-based attendance with instant analytics</li>
            </ul>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Factskoot
        </p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="h-3.5 w-3.5" /> Back home
          </Link>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium">
            Create your TeeBee.
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {invitePreview
              ? `You've been invited to join ${invitePreview.institution_name} as ${invitePreview.role}.`
              : "Free for individual teachers during beta."}
          </p>

          <Button
            onClick={googleSignIn}
            variant="outline"
            className="mt-6 w-full h-11 rounded-full"
            data-testid="signup-google-button"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px bg-border flex-1" /> or with email <span className="h-px bg-border flex-1" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Priya Reddy"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="signup-name-input"
              />
            </div>
            <div>
              <Label htmlFor="institution">Institution (optional)</Label>
              <Input
                id="institution"
                value={institution}
                onChange={(e) => setInstitution(e.target.value)}
                placeholder="Sunshine High School"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="signup-institution-input"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@school.edu"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="signup-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-1.5 h-11 rounded-xl"
                data-testid="signup-password-input"
              />
            </div>
            <label className="flex items-start gap-2.5 text-[12px] cursor-pointer" data-testid="signup-consent-row">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-violet-600 cursor-pointer"
                data-testid="signup-consent-checkbox"
              />
              <span className="text-muted-foreground leading-relaxed">
                I agree to TeeBee&apos;s{" "}
                <Link to="/legal/terms" className="text-foreground underline-offset-2 hover:underline">Terms</Link>,{" "}
                <Link to="/legal/privacy" className="text-foreground underline-offset-2 hover:underline">Privacy Policy</Link>,{" "}
                <Link to="/legal/ai-disclaimer" className="text-foreground underline-offset-2 hover:underline">AI Disclaimer</Link>, and the{" "}
                <Link to="/legal/coins-policy" className="text-foreground underline-offset-2 hover:underline">TeeBee Coins Policy</Link>.
              </span>
            </label>
            <Button
              type="submit"
              disabled={busy || !agreed}
              className="w-full h-11 rounded-full"
              data-testid="signup-submit-button"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-sm text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-violet-600 hover:underline" data-testid="signup-to-login">
              Sign in
            </Link>
          </p>
        </div>
      </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.7 3.3 14.6 2.2 12 2.2 6.5 2.2 2 6.7 2 12.2s4.5 10 10 10c5.8 0 9.6-4.1 9.6-9.8 0-.7-.1-1.2-.2-1.7H12z" />
    </svg>
  );
}
